import { z } from 'zod';
import { AttendeeResearchSchema } from './meetings-page.js';
import { TEXT_INPUT_LIMITS } from './text-input-limits.js';
export const ClientNotesSchema = z.string().max(TEXT_INPUT_LIMITS.clientNote, {
    message: `Client Notes must be ${TEXT_INPUT_LIMITS.clientNote} characters or fewer`,
});
export const DealStageSchema = z.string().min(1);
// =============================================================================
// Match Strategies (for meeting-to-client linking)
// =============================================================================
export const MatchStrategy = {
    EMAIL_DOMAIN: 'email_domain',
    CONTACT_EMAIL: 'contact_email',
    GEMINI: 'gemini',
    TITLE_KEYWORD: 'title_keyword',
    MANUAL: 'manual',
};
export const MatchStrategySchema = z.enum(Object.values(MatchStrategy));
// Display metadata for UI. Record<MatchStrategy, ...> so a new strategy is a
// compile error until it gets a label here.
export const MatchStrategyMetadata = {
    email_domain: { label: 'Email Domain' },
    contact_email: { label: 'Contact Email' },
    gemini: { label: 'Gemini' },
    title_keyword: { label: 'Title Keyword' },
    manual: { label: 'Manual' },
};
/** Accept website hostnames as users commonly enter them and store full URLs. */
const ClientWebsiteSchema = z
    .union([
    z.string().trim().url().max(500),
    z.string()
        .trim()
        .regex(/^(?=.{1,253}$)(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+[a-z]{2,63}$/i)
        .transform((value) => `https://${value}`),
])
    .nullable()
    .optional();
export const ClientSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    name: z.string().min(1).max(255),
    domain: z.string().max(255).nullable().optional(),
    domains: z.array(z.string()).default([]),
    industry: z.string().max(100).nullable().optional(),
    website: ClientWebsiteSchema,
    notes: ClientNotesSchema.nullable().optional(),
    starred: z.boolean().default(false),
    logoFileId: z.string().uuid().nullable().optional(),
    logoUrl: z.string().url().nullable().optional(), // Computed field
    employeeCount: z.string().max(20).nullable().optional(),
    country: z.string().max(2).nullable().optional(),
    createdByUserId: z.string().uuid().nullable().optional(),
    createdByUserName: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable().optional(),
    deletedAt: z.string().datetime().nullable().optional(),
    permanentDeletionAt: z.string().datetime().nullable().optional(),
});
export const ClientScoreBlockSchema = z.object({
    overall: z.number().int().min(0).max(100),
    engagement: z.number().int().min(0).max(100),
    pipeline: z.number().int().min(0).max(100),
    reach: z.number().int().min(0).max(100),
    summary: z.string().nullable(),
});
// =============================================================================
// Contact-Client Associations
// =============================================================================
export const ContactRelationship = {
    EMPLOYEE: 'employee',
    CONSULTANT: 'consultant',
    PARTNER: 'partner',
    ADVISOR: 'advisor',
    PARTICIPANT: 'participant',
};
export const ContactRelationshipSchema = z.enum(Object.values(ContactRelationship));
export const ContactRelationshipMetadata = {
    employee: {
        label: 'Employee',
        description: 'Works directly at the client organization',
    },
    consultant: {
        label: 'Consultant',
        description: 'Third-party consultant engaged on the account (e.g. a management firm helping close the deal)',
    },
    partner: {
        label: 'Partner',
        description: 'Partner organization collaborating on the account',
    },
    advisor: {
        label: 'Advisor',
        description: 'External advisor influencing the account',
    },
    participant: {
        label: 'Participant',
        description: 'Involved in conversations without a formal role',
    },
};
/**
 * Relationship options for a `Select`, in enum order. Single source of truth for
 * the picker. `value` is widened to `string` so it drops straight into shared-ui's
 * `SelectOption[]` without a cast.
 */
export const ContactRelationshipOptions = Object.entries(ContactRelationshipMetadata).map(([value, meta]) => ({ value, label: meta.label }));
export const ContactRelationshipColors = {
    employee: {
        text: 'text-emerald-400',
        bg: 'bg-emerald-500/15',
        border: 'border-emerald-500/25',
    },
    consultant: {
        text: 'text-violet-400',
        bg: 'bg-violet-500/15',
        border: 'border-violet-500/25',
    },
    partner: {
        text: 'text-blue-400',
        bg: 'bg-blue-500/15',
        border: 'border-blue-500/25',
    },
    advisor: {
        text: 'text-amber-400',
        bg: 'bg-amber-500/15',
        border: 'border-amber-500/25',
    },
    participant: {
        text: 'text-slate-300',
        bg: 'bg-slate-400/15',
        border: 'border-slate-400/25',
    },
};
export const ContactClientAssociationSchema = z.object({
    id: z.string().uuid(),
    contactId: z.string().uuid(),
    clientId: z.string().uuid(),
    orgId: z.string(),
    relationship: ContactRelationshipSchema,
    clientName: z.string().optional(),
    clientLogoUrl: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// =============================================================================
// Contact (Person at a client organization)
// =============================================================================
// How the contact was created (for permission tracking)
export const ContactCreationSource = {
    MANUAL: 'manual',
    EMAIL_SYNC: 'email_sync',
    CALENDAR_SYNC: 'calendar_sync',
    CRM_SYNC: 'crm_sync',
};
export const ContactCreationSourceSchema = z.enum(Object.values(ContactCreationSource));
// Display metadata for UI. Record<ContactCreationSource, ...> so a new source is a
// compile error until it gets a label here.
export const ContactCreationSourceMetadata = {
    manual: { label: 'Manual' },
    email_sync: { label: 'Email Sync' },
    calendar_sync: { label: 'Calendar Sync' },
    crm_sync: { label: 'CRM Sync' },
};
export const ContactNameSource = {
    MANUAL: 'manual',
    CRM: 'crm',
    EMAIL_DISPLAY: 'email_display',
    CALENDAR_DISPLAY: 'calendar_display',
    AI_THREAD: 'ai_thread',
    FALLBACK: 'fallback',
    LEGACY_PROTECTED: 'legacy_protected',
};
export const ContactNameSourceSchema = z.enum(Object.values(ContactNameSource));
export const EmailParticipantSchema = z.object({
    email: z.string().email(),
    name: z.string().nullable().optional(),
});
export const ContactSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    name: z.string().min(1).max(255),
    email: z.string().email().max(255).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    title: z.string().max(255).nullable().optional(),
    linkedinUrl: z.string().url().max(500).nullable().optional(),
    profilePictureUrl: z.string().url().nullable().optional(),
    // Permission tracking fields
    source: ContactCreationSourceSchema.default('manual'),
    nameSource: ContactNameSourceSchema.default('manual'),
    nameLockedAt: z.string().datetime().nullable().optional(),
    nameLockedByUserId: z.string().uuid().nullable().optional(),
    createdByUserId: z.string().uuid().nullable().optional(),
    ownerUserId: z.string().nullable().optional(),
    isEnriched: z.boolean().default(false),
    lastSyncedAt: z.string().datetime().nullable().optional(),
    // AI research data
    researchData: AttendeeResearchSchema.nullable().optional(),
    researchedAt: z.string().datetime().nullable().optional(),
    // Client associations
    associations: z.array(ContactClientAssociationSchema).optional().default([]),
    // Computed field for frontend
    canEdit: z.boolean().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable().optional(),
});
export const DealSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    clientId: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    name: z.string().min(1).max(255),
    value: z.number().nullable().optional(),
    projectedRevenue: z.number().nullable().optional(),
    pipelineId: z.string().uuid(),
    stage: DealStageSchema,
    stageColor: z.string().nullable().optional(),
    probability: z.number().min(0).max(100).nullable().optional(),
    closeOutcome: z.string().nullable().optional(),
    closedAt: z.string().datetime().nullable().optional(),
    closeReason: z.string().nullable().optional(),
    closeNotes: z.string().nullable().optional(),
    stageSubdivision: z.string().nullable().optional(),
    stageReason: z.string().nullable().optional(),
    stageNotes: z.string().nullable().optional(),
    subdivision: z.string().nullable().optional(),
    forecastCategory: z.string().nullable().optional(),
    expectedCloseDate: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable().optional(),
    deletedAt: z.string().datetime().nullable().optional(),
    archiveReason: z.string().nullable().optional(),
    lifecycleAction: z.enum(['client_archived', 'client_deleted']).nullable().optional(),
    lifecyclePreviousState: z.enum(['open', 'closed', 'archived']).nullable().optional(),
});
export const MeetingClientLinkSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    meetingId: z.string().uuid(),
    clientId: z.string().uuid(),
    matchStrategy: MatchStrategySchema.nullable().optional(),
    confidenceScore: z.number().min(0).max(100).nullable().optional(),
    linkedAt: z.string().datetime(),
    linkedBy: z.string().nullable().optional(),
});
export const MatchResultSchema = z.object({
    clientId: z.string().uuid(),
    clientName: z.string(),
    strategy: MatchStrategySchema,
    confidence: z.number().min(0).max(100),
    reason: z.string(),
});
// =============================================================================
// Personal Domain Validation
// =============================================================================
/** Common personal/free email provider domains that should not be used as client domains */
export const COMMON_EMAIL_PROVIDERS = new Set([
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'live.com',
    'msn.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'protonmail.com',
    'proton.me',
    'zoho.com',
    'yandex.com',
    'mail.com',
]);
/** Check if a domain belongs to a common personal email provider */
export function isPersonalDomain(domain) {
    return COMMON_EMAIL_PROVIDERS.has(domain.toLowerCase());
}
export const CreateClientRequestSchema = z.object({
    name: z.string().min(1).max(255),
    domain: z.string().max(255).nullable().optional(),
    domains: z.array(z.string()).optional(),
    linkExistingContactsByDomain: z.boolean().optional(),
    industry: z.string().max(100).nullable().optional(),
    country: z.string().max(2).nullable().optional(),
    website: ClientWebsiteSchema,
    notes: ClientNotesSchema.nullable().optional(),
    employeeCount: z.string().max(20).nullable().optional(),
    createdAt: z.string().nullable().optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
}).superRefine((data, ctx) => {
    if (data.domain && isPersonalDomain(data.domain)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Personal email domain not allowed: ${data.domain.toLowerCase()}`,
            path: ['domain'],
        });
    }
    if (data.domains) {
        const personalDomains = data.domains.filter((d) => isPersonalDomain(d));
        if (personalDomains.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Personal email domain(s) not allowed: ${personalDomains.map((d) => d.toLowerCase()).join(', ')}`,
                path: ['domains'],
            });
        }
    }
});
export const UpdateClientRequestSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    domain: z.string().max(255).nullable().optional(),
    domains: z.array(z.string()).optional(),
    industry: z.string().max(100).nullable().optional(),
    country: z.string().max(2).nullable().optional(),
    website: ClientWebsiteSchema,
    notes: ClientNotesSchema.nullable().optional(),
    starred: z.boolean().optional(),
    logoFileId: z.string().uuid().nullable().optional(),
    employeeCount: z.string().max(20).nullable().optional(),
}).superRefine((data, ctx) => {
    if (data.domain && isPersonalDomain(data.domain)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Personal email domain not allowed: ${data.domain.toLowerCase()}`,
            path: ['domain'],
        });
    }
    if (data.domains) {
        const personalDomains = data.domains.filter((d) => isPersonalDomain(d));
        if (personalDomains.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Personal email domain(s) not allowed: ${personalDomains.map((d) => d.toLowerCase()).join(', ')}`,
                path: ['domains'],
            });
        }
    }
});
export const CreateContactRequestSchema = z.object({
    name: z.string().min(1).max(255),
    email: z.string().email().max(255).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    title: z.string().max(255).nullable().optional(),
    linkedinUrl: z.string().url().max(500).nullable().optional(),
    source: ContactCreationSourceSchema.optional(),
    createdAt: z.string().nullable().optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
});
export const UpdateContactRequestSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    email: z.string().email().max(255).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    title: z.string().max(255).nullable().optional(),
    linkedinUrl: z.string().url().max(500).nullable().optional(),
});
export const CreateDealRequestSchema = z.object({
    clientId: z.string().uuid(),
    name: z.string().min(1).max(255),
    value: z.number().nullable().optional(),
    projectedRevenue: z.number().nullable().optional(),
    expectedCloseDate: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
    productIds: z.array(z.string().uuid()).default([]),
});
export const UpdateDealRequestSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    value: z.number().nullable().optional(),
    projectedRevenue: z.number().nullable().optional(),
    stage: DealStageSchema.optional(),
    expectedCloseDate: z.string().nullable().optional(),
});
export const LinkMeetingRequestSchema = z.object({
    clientId: z.string().uuid(),
    matchStrategy: MatchStrategySchema.optional(),
    confidenceScore: z.number().min(0).max(100).optional(),
});
// =============================================================================
// B2B Buyer Roles (MEDDIC methodology)
// =============================================================================
export const BuyerRole = {
    CHAMPION: 'champion',
    DECISION_MAKER: 'decision_maker',
    INFLUENCER: 'influencer',
    END_USER: 'end_user',
    GATEKEEPER: 'gatekeeper',
    ECONOMIC_BUYER: 'economic_buyer',
    SIGNER: 'signer',
};
export const BuyerRoleSchema = z.enum(Object.values(BuyerRole));
export const BuyerRoleMetadata = {
    champion: {
        label: 'Champion',
        description: 'Internal advocate who actively promotes your solution',
        color: 'emerald',
        icon: 'star',
    },
    decision_maker: {
        label: 'Decision Maker',
        description: 'Has authority to approve the purchase',
        color: 'amber',
        icon: 'crown',
    },
    influencer: {
        label: 'Influencer',
        description: 'Impacts the decision through expertise or relationships',
        color: 'purple',
        icon: 'sparkles',
    },
    end_user: {
        label: 'End User',
        description: 'Will use the product/service directly',
        color: 'blue',
        icon: 'user',
    },
    gatekeeper: {
        label: 'Gatekeeper',
        description: 'Controls access to decision makers and information',
        color: 'slate',
        icon: 'shield',
    },
    economic_buyer: {
        label: 'Economic Buyer',
        description: 'Controls budget allocation',
        color: 'cyan',
        icon: 'banknotes',
    },
    signer: {
        label: 'Signer',
        description: 'Authorized to sign contracts',
        color: 'rose',
        icon: 'pencil',
    },
};
export const BuyerRoleColors = {
    champion: {
        text: 'text-emerald-400',
        bg: 'bg-emerald-500/15',
        border: 'border-emerald-500/25',
        iconBg: 'bg-emerald-500/25',
        gradient: 'from-emerald-500/20 to-emerald-600/10',
        checkbox: 'text-emerald-400 data-checked:border-emerald-500 data-checked:bg-emerald-500/20',
    },
    decision_maker: {
        text: 'text-amber-400',
        bg: 'bg-amber-500/15',
        border: 'border-amber-500/25',
        iconBg: 'bg-amber-500/25',
        gradient: 'from-amber-500/20 to-amber-600/10',
        checkbox: 'text-amber-400 data-checked:border-amber-500 data-checked:bg-amber-500/20',
    },
    influencer: {
        text: 'text-purple-400',
        bg: 'bg-purple-500/15',
        border: 'border-purple-500/25',
        iconBg: 'bg-purple-500/25',
        gradient: 'from-purple-500/20 to-purple-600/10',
        checkbox: 'text-purple-400 data-checked:border-purple-500 data-checked:bg-purple-500/20',
    },
    end_user: {
        text: 'text-blue-400',
        bg: 'bg-blue-500/15',
        border: 'border-blue-500/25',
        iconBg: 'bg-blue-500/25',
        gradient: 'from-blue-500/20 to-blue-600/10',
        checkbox: 'text-blue-400 data-checked:border-blue-500 data-checked:bg-blue-500/20',
    },
    gatekeeper: {
        text: 'text-slate-300',
        bg: 'bg-slate-400/15',
        border: 'border-slate-400/25',
        iconBg: 'bg-slate-400/25',
        gradient: 'from-slate-400/20 to-slate-500/10',
        checkbox: 'text-slate-300 data-checked:border-slate-400 data-checked:bg-slate-400/20',
    },
    economic_buyer: {
        text: 'text-cyan-400',
        bg: 'bg-cyan-500/15',
        border: 'border-cyan-500/25',
        iconBg: 'bg-cyan-500/25',
        gradient: 'from-cyan-500/20 to-cyan-600/10',
        checkbox: 'text-cyan-400 data-checked:border-cyan-500 data-checked:bg-cyan-500/20',
    },
    signer: {
        text: 'text-rose-400',
        bg: 'bg-rose-500/15',
        border: 'border-rose-500/25',
        iconBg: 'bg-rose-500/25',
        gradient: 'from-rose-500/20 to-rose-600/10',
        checkbox: 'text-rose-400 data-checked:border-rose-500 data-checked:bg-rose-500/20',
    },
};
// =============================================================================
// Deal Contact (Junction entity)
// =============================================================================
export const ContactSource = {
    MEETING: 'meeting',
    EMAIL: 'email',
    MANUAL: 'manual',
};
export const DealContactSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    dealId: z.string().uuid(),
    contactId: z.string().uuid(),
    roles: z.array(BuyerRoleSchema).default([]),
    source: z.enum(Object.values(ContactSource)).default('meeting'),
    sourceId: z.string().uuid().nullable().optional(),
    researchData: z.object({
        linkedinUrl: z.string().nullable().optional(),
        linkedinSummary: z.string().nullable().optional(),
        shortSummary: z.string().nullable().optional(),
        currentTitle: z.string().nullable().optional(),
        currentCompany: z.string().nullable().optional(),
        companyBackground: z.string().nullable().optional(),
        interests: z.array(z.string()).optional(),
        potentialTalkingPoints: z.array(z.string()).optional(),
        recentNews: z.array(z.string()).optional(),
    }).nullable().optional(),
    researchedAt: z.string().datetime().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const UpdateContactRolesRequestSchema = z.object({
    roles: z.array(BuyerRoleSchema),
});
export const UpsertDealContactRequestSchema = z.object({
    contactId: z.string().uuid(),
    roles: z.array(BuyerRoleSchema).optional(),
    source: z.enum(Object.values(ContactSource)).optional(),
    sourceId: z.string().uuid().optional(),
});
export const DealAssignmentSchema = z.object({
    dealId: z.string().uuid(),
    roles: z.array(BuyerRoleSchema),
});
export const CreateContactWithDealsRequestSchema = z.object({
    name: z.string().min(1).max(255),
    email: z.string().email().max(255).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
    title: z.string().max(255).nullable().optional(),
    linkedinUrl: z.string().url().max(500).nullable().optional(),
    dealAssignments: z.array(DealAssignmentSchema).optional(),
});
